import type { BookingComment, ChargeType, Infant } from "@repo/ui/types/booking";

import type { PassengerAddress, PassengerType } from "@repo/store/slices";

export interface EligibilityResponse {
  eligible: boolean;
}

export enum PaymentStatus {
  UnderPaid = 0,
  PaidInFull = 1,
  OverPaid = 2,
}

export enum ChannelType {
  Default = 0,
  Direct = 1,
  Web = 2,
  Gds = 3,
  Api = 4,
  DigitalApi = 5,
  DigitalWeb = 6,
  Ndc = 7,
}

export interface BookingSummary {
  recordLocator: string;
  currencyCode: string;
  paymentStatus?: PaymentStatus;
  owningCarrierCode: string;
  breakdown: Breakdown;
  promotionCode: string | null;
  passengers: Passenger[];
  passengerCounts: PassengerCounts;
  contacts: Contact[];
  journeys: Journey[];
  payments: Payment[];
  isRoundTrip: boolean;
  userRole?: UserRoleType;
  isCheckInBagFullForOutbound: boolean;
  isCheckInBagFullForReturn: boolean;
  isLargeCabinFullForOutbound: boolean;
  isLargeCabinFullForReturn: boolean;
  isOversizedBagFullForOutbound: boolean;
  isOversizedBagFullForReturn: boolean;
  comments?: BookingComment[];
  userKey?: string;
  createdUserKey?: string;
  queues?: { code: string }[];
  channelType?: number;
}

export interface UserRoleType {
  roleName: string;
  roleCode: string;
  parentRoleCode: string;
}

export interface Passenger {
  passengerKey: string;
  nameDetails?: NameDetails;
  passengerTypeCode: PassengerType;
  name: string;
  nationality: string | null;
  residentCountry: string | null;
  passengerAddressKey?: string | null;
  emailAddress: string | null;
  countryCode: string | null;
  callingCode?: string | null;
  phone: string | null;
  city: string | null;
  address: string | null;
  addresses: PassengerAddress[] | null;
  stationCode?: string | null;
  gender: string | number;
  dateOfBirth: string;
  travelDocuments: TravelDocument[] | null;
  infant: Infant | null;
  serviceCharges?: Record<string, unknown>;
  aadvantageNumber?: string | null;
  outboundJourneySSRs?: JourneySSR;
  inboundJourneySSRs?: JourneySSR;
  inboundSeats?: PassengerSeat[];
  outboundSeats?: PassengerSeat[];
  passengerTaxes?: Charge[];
  passengerTaxesAmount?: number;
  passengerFees?: PassengerFee[];
}

export interface PassengerSeat {
  unitDesignator: string;
  seatInformation: SeatInformation;
  unitKey: string;
  crossReferenceSeatingPreference: unknown;
  isPending: boolean;
  seatmapReference: string;
  arrivalStation: string;
  departureStation: string;
}

export interface SeatInformation {
  deck: number;
  seatSet: number;
  propertyList: {
    WINDOW?: string;
    WING?: string;
    BRDZONE?: string;
    RESTRICT?: string;
    INFANT?: string;
    TCC?: string;
  };
}

export interface JourneySSR {
  bundleSsrs: BundleSsr[];
  individualSsrs: BundleSsr[];
  infantSSRs: BundleSsr[];
  bundle: BundleSsr | null;
}

export interface BundleSsr {
  code: string;
  name: string;
  amount: number;
  foreignAmount: number;
  currencyCode: string;
  foreignCurrencyCode: string;
}

export interface TravelDocument {
  passengerTravelDocumentKey: string;
  documentTypeCode: string;
  birthCountry: string | null;
  issuedByCode: string;
  name: NameDetails;
  nationality: string | null;
  expirationDate: string;
  number: string;
  issuedDate: string;
  gender: number | string;
  dateOfBirth: string;
}

export interface NameDetails {
  first: string;
  middle: string | null;
  last: string;
  title: string | null;
  suffix: string | null;
}

export interface PassengerCounts {
  byType: {
    adult: number;
    child: number;
    infant: number;
  };
  total: number;
}

export interface Contact {
  name: string;
  firstname: string;
  middlename: string;
  lastname: string;
  phone: string;
  email: string;
  contactTypeCode?: string;
}

export interface CheckInStatus {
  isClosed?: boolean;
  isDisabled: boolean;
  isTooEarly: boolean;
  isTooLate: boolean;
  isImminent?: boolean;
  message?: string;
}

export interface Journey {
  type: "outbound" | "inbound";
  journeyKey: string;
  departure: LocationDate;
  arrival: LocationDate;
  flight: JourneyFlightSummary;
  hasPlaneChange: boolean;
  pendingPassengersToCheckIn: PendingCheckIn;
  fareClass: string;
  serviceCharges?: Charge[];
  segments: Segment[];
  waitingTime?: Duration;
  checkInStatus: CheckInStatus;
}

export interface LocationDate {
  code: string;
  name: string;
  date: string;
}

export interface JourneyFlightSummary {
  type?: "direct" | "stopover" | "connecting";
  number: string;
  duration: Duration;
}

export interface SegmentFlightSummary {
  type?: "direct" | "stopover" | "connecting";
  number: string;
  carrierCode: string;
  carrierName: string;
  identifier: string | number;
  opSuffix?: unknown;
  duration: Duration;
  reference?: string;
}

export interface Duration {
  hours: number;
  minutes: number;
}

export interface PendingCheckIn {
  count: number;
  passengers: { passengerKey: string; hasInfant: boolean }[];
}

export interface Charge {
  amount: number;
  code?: string;
  detail?: string | null;
  type?: number | string | ChargeType;
  collectType?: number;
  currencyCode: string;
  foreignCurrencyCode?: string;
  foreignAmount?: number;
  ticketCode?: string | null;
  chargeCode?: string | null;
  chargeDetail?: string | null;
  chargeName?: string | null;
}

export interface BreakdownCharge {
  quantity?: number;
  name?: string;
  code?: string;
  ssrCode?: string;
  // Marks a synthesized special-assistance (wheelchair/oxygen) line so PricingList can
  // always show its $0 amount instead of deferring to CMS's generic "Incluido" label,
  // even when merged into the same array as real backend-confirmed items.
  isSpecialAssistance?: boolean;
  amount: number | string;
  baseAirfareAmount?: number;
  discountedBaseAirfareAmount?: number;
  adminFeeAmount?: number;
  totalTaxAmount?: number;
  totalDiscountAmount?: number;
  totalAmount?: number;
  foreignAmount?: number;
  currencyCode?: string;
  foreignCurrencyCode?: string;
  chargeType?: ChargeType;
  taxCode?: string;
  iataCode?: string;
}
export interface Segment {
  segmentKey: string;
  isSeatmapViewable: boolean;
  departure: LocationDate;
  arrival: LocationDate;
  flight: SegmentFlightSummary;
  hasPlaneChange: boolean;
  legs: Leg[];
  serviceCharges?: Charge[];
  designator?: SegmentDesignator;
  passengerSegment?: Record<string, PassengerSegment>;
}

export interface SegmentDesignator {
  origin: string;
  destination: string;
  departure: string;
  arrival: string;
}

interface Seats {
  unitDesignator: string;
  [key: string]: unknown;
}

export interface PassengerSegment {
  seats: Seats[];
  passengerKey: string;
  activityDate: string | null;
  boardingSequence: number | null;
  createdDate: string | null;
  liftStatus: number;
  modifiedDate: string | null;
  overBookIndicator: number;
  priorityDate: string | null;
  timeChanged: boolean;
  verifiedTravelDocs: unknown;
  sourcePointOfSale: unknown;
  pointOfSale: unknown;
  ssrs: SSR[];
  tickets: unknown[];
  bags: unknown[];
  scores: unknown[];
  boardingPassDetail: unknown;
  hasInfant: boolean;
  seatPreferences: {
    seat: number;
    travelClass: number;
    advancedPreferences: unknown[];
  };
  bundleCode: string;
  verifiedTravelDocuments: unknown;
  referenceNumber: number;
  baggageGroupNumber: number | null;
}

export interface SSR {
  isConfirmed: boolean;
  isConfirmingUnheld: boolean;
  note: string | null;
  ssrDuration: number;
  ssrKey: string;
  count: number;
  ssrCode: string;
  feeCode: string;
  inBundle: boolean;
  passengerKey: string;
  ssrDetail: unknown;
  ssrNumber: number;
  market: {
    identifier: {
      identifier: string;
      carrierCode: string;
      opSuffix: string | null;
    };
    destination: string;
    origin: string;
    departureDate: string;
  };
  price?: string | number;
}

export interface Leg {
  legKey: string;
  departure: LocationDateUTC;
  arrival: LocationDateUTC;
  inventoryType: string;
  legInfo?: LegInfo;
  // null when the leg is cancelled
  operationsInfo?: LegOperationsInfo | null;
}

export interface LegOperationsInfo {
  departureTimes?: {
    scheduled: string;
    estimated: string | null;
  } | null;
  estimatedArrivalTime?: string | null;
  // Scheduled arrival (STA), local to the arrival station.
  standardArrivalTime?: string | null;
}

export interface LegInfo {
  // 2 = cancelled. Unrelated to any booking-level status field.
  status?: number;
  departureTimeUtc?: string;
  arrivalTimeUtc?: string;
  arrivalTimeVariant?: number;
  departureTimeVariant?: number;
  arrivalTime?: string;
  departureTime?: string;
  departureTerminal?: string | null;
  arrivalTerminal?: string | null;
}

export interface LocationDateUTC extends LocationDate {
  dateUTC: string;
}

export interface Payment {
  paymentKey: string;
  paymentCode: string;
  paymentType: string;
  paymentStatus: string;
  paymentDetails: PaymentDetails;
}

export interface PaymentDetails {
  transactionId: number;
  transactionDate: string;
  transactionAmount: number;
  authorizationCode: string | null;
  installments: number;
  currencyCode: string;
  lastFourDigits: string | null;
  accountNumber: string | null;
  card?: string | null;
}

export interface ServiceCharge {
  amount: number;
  code: string;
  detail: string;
  type: number | string;
  collectType: number;
  currencyCode: string;
  foreignCurrencyCode: string;
  foreignAmount: number;
  ticketCode: string | null;
  name?: string | null;
}

export interface ChargeGroup {
  total: number | null;
  taxes: number | null;
  adjustments: number | null;
  charges: Charge[];
}

export interface PassengerTotals {
  services: ChargeGroup | null;
  specialServices: ChargeGroup | null;
  seats: ChargeGroup | null;
  upgrades: ChargeGroup | null;
  spoilage: ChargeGroup | null;
  nameChanges: ChargeGroup | null;
  convenience: ChargeGroup | null;
  infant: ChargeGroup | null;
  totalCost: number;
}

export interface PassengerBreakdown extends PassengerTotals {
  passengerKey: string;
}

export interface JourneyTotals {
  totalAmount: number;
  totalPoints: number;
  totalTax: number;
  totalDiscount: number;
}

export interface JourneyBreakdown extends JourneyTotals {
  journeyKey: string;
}

export interface JourneysBreakdown {
  type: string;
  passengers: BreakdownCharge[];
  baggages: BreakdownCharge[];
  seats: BreakdownCharge[];
  optionals: BreakdownCharge[];
  passengersTotal: BreakdownCharge | null;
  totalAmount?: number;
  totalPoints?: number;
  totalTax?: number;
  totalDiscount?: number;
  baggagesTotal: BreakdownCharge | null;
  seatsTotal: BreakdownCharge | null;
  optionalsTotal: BreakdownCharge | null;
}

export interface AddOnTotals {
  car: number | null;
  hotel: number | null;
  activities: number | null;
}

export interface Breakdown {
  balanceDue: number;
  pointsBalanceDue: number;
  authorizedBalanceDue: number;
  totalAmount: number;
  totalPoints: number;
  totalToCollect: number;
  totalPointsToCollect: number;
  totalCharged: number;
  passengerTotals: PassengerTotals;
  passengers: Record<string, PassengerBreakdown>;
  journeyTotals: JourneyTotals;
  journeys: JourneysBreakdown[];
  addOnTotals: AddOnTotals;
  ratesAndTaxes: BreakdownCharge[];
  discounts: BreakdownCharge[];
}

export interface RetrieveBookingRequest {
  emailOrLastName: string;
  recordLocator: string;
}

export type totalAmountType = string | number | null;

export interface PassengerFee {
  type: number;
  ssrCode: string | null;
  ssrNumber: number;
  paymentNumber: number;
  isConfirmed: boolean;
  isConfirming: boolean;
  isConfirmingExternal: boolean;
  code: string | null;
  detail: string | null;
  passengerFeeKey: string;
  override: boolean;
  flightReference: string;
  note: string | null;
  createdDate: string;
  isProtected: boolean;
  serviceCharges: ServiceCharge[];
}
